import { useState, useContext } from 'react';
import {
    Box,
    Grid,
    Card,
    CardHeader,
    CardContent,
    Chip,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButton,
    SttQR,
    SttDialog,
    SttFormSelect,
} from '../../../sporttia/all';
import { formatPriceByLocale, fullName } from '../../../lib/utils';
import constants from '../../../config/constants';
import { isDataphonePayment } from '../../../components/paymentModal/refundDataphoneVisibility';

export default function Occupants({
    occupants,
    cancelPrivilege,
    idOwner,
}: any) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [occupantsValues, setOccupantsValues] = useState(occupants);

    // Entradas impersonales: ocupante pendiente de elegir forma de devolución
    // (efectivo/datáfono) al cancelar. Una entrada impersonal viene marcada
    // desde el backend; su devolución solo puede ser efectivo o datáfono.
    const [refundOccupant, setRefundOccupant] = useState<any>(null);
    const [refundForm, setRefundForm] = useState<string | null>(null);

    const navigate = useNavigate();

    const isImpersonalOccupant = (occupant: any) =>
        occupant?.impersonal === true;

    /**
     * Actually cancel the ticket. For impersonal entries `refundFormValue`
     * (CASH/DATAFONO) is sent and the refund dialog is the confirmation; for
     * nominal occupants we keep the plain confirmation and no refundForm.
     */
    function performDelete(occupantId: any, refundFormValue?: string) {
        cxt.api('DELETE', `/bookings/occupants/${occupantId}`, {
            params: {
                cancelBookingOnLastOccupant: true,
                ...(refundFormValue ? { refundForm: refundFormValue } : {}),
            },
            success: () => {
                cxt.showMessage('S', t('messages.deleted'));
                const newOccupantsValue = occupantsValues.filter(
                    (occupant: any) => occupant.id !== occupantId,
                );
                setOccupantsValues(newOccupantsValue);
                if (newOccupantsValue.length === 0) {
                    navigate(-1);
                }
            },
            confirmation: refundFormValue === undefined,
        });
    }

    function cancelTicket(occupant: any) {
        // Entrada impersonal: el cajero elige efectivo o datáfono (nunca
        // monedero). El diálogo de devolución hace de confirmación.
        if (isImpersonalOccupant(occupant)) {
            setRefundForm(null);
            setRefundOccupant(occupant);
            return;
        }

        performDelete(occupant.id);
    }

    return (
        <Box mt={2}>
            <SttDialog
                open={refundOccupant !== null}
                onClose={() => setRefundOccupant(null)}
                title={t('impersonalTicket.refundTitle')}
                content={
                    <Box>
                        <Typography variant="body1" mb={2}>
                            {t('impersonalTicket.refundWarning')}
                        </Typography>
                        <SttFormSelect
                            caption={t('SelectOneOption')}
                            defVal={refundForm}
                            onChange={({ value }: { value: unknown }) =>
                                setRefundForm(value as string)
                            }
                            options={[
                                {
                                    caption: t('impersonalTicket.refundCash'),
                                    value: constants.payment.paymentForms.cash
                                        .name,
                                },
                                // El datáfono solo se ofrece si el cobro
                                // original se hizo con datáfono; el efectivo
                                // siempre está disponible.
                                ...(isDataphonePayment(
                                    refundOccupant?.paymentForm,
                                )
                                    ? [
                                          {
                                              caption: t(
                                                  'impersonalTicket.refundDatafono',
                                              ),
                                              value: constants.payment
                                                  .paymentForms.dataphone.name,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </Box>
                }
                buttons={[
                    {
                        type: 'accept',
                        caption: t('Accept'),
                        disabled: refundForm === null,
                        onClick: () => {
                            if (refundOccupant && refundForm) {
                                performDelete(refundOccupant.id, refundForm);
                                setRefundOccupant(null);
                            }
                        },
                    },
                    {
                        type: 'close',
                        onClick: () => setRefundOccupant(null),
                    },
                ]}
            />

            <Grid container spacing={3}>
                {occupantsValues?.map((occupant: any) => (
                    <Grid key={occupant.id} size={{ xs: 12, sm: 12, md: 12 }}>
                        <Card>
                            <CardHeader title={fullName(occupant.user)} />
                            <CardContent>
                                {/* --- Rate & price --- */}
                                <Box>
                                    {occupant.rate?.map((price: any) => (
                                        <Chip
                                            key={price.id}
                                            label={`${
                                                price.name
                                            } [ ${formatPriceByLocale(
                                                price.price,
                                                price.priceShape?.currency,
                                                price.priceShape?.locale,
                                            )} ]`}
                                        />
                                    ))}
                                </Box>
                                {occupant.bonoMov?.id && (
                                    <Box>
                                        <Chip
                                            key={occupant?.bonoMov?.id}
                                            label={`${occupant?.bonoMov?.boleto?.bono?.name}`}
                                        />
                                    </Box>
                                )}
                                {occupant.unlockers?.[0]?.type ===
                                    constants.unlocker.types.QR && (
                                    <Box mt={1} sx={{ textAlign: 'center' }}>
                                        <SttQR
                                            unlocker={occupant.unlockers[0]}
                                        />
                                    </Box>
                                )}

                                {occupant.unlockers?.[0]?.type ===
                                    constants.unlocker.types.PIN && (
                                    <Box mt={1}>
                                        <Typography variant="body1">
                                            PIN: {occupant.unlockers[0].code}
                                        </Typography>
                                        <Box mt={1}>
                                            <Typography variant="body2">
                                                *{' '}
                                                {t(
                                                    'booking.rememberToPressHash',
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {occupant.unlockers?.[1]?.type ===
                                    constants.unlocker.types.QR && (
                                    <Box mt={1} sx={{ textAlign: 'center' }}>
                                        <SttQR
                                            unlocker={occupant.unlockers[1]}
                                        />
                                    </Box>
                                )}

                                {occupant.unlockers?.[1]?.type ===
                                    constants.unlocker.types.PIN && (
                                    <Box mt={1}>
                                        <Typography variant="body1">
                                            PIN: {occupant.unlockers[1].code}
                                        </Typography>
                                        <Box mt={1}>
                                            <Typography variant="body2">
                                                *{' '}
                                                {t(
                                                    'booking.rememberToPressHash',
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {cxt.logged &&
                                    (cxt.user?.role ===
                                        constants.roles.sportcenter ||
                                        (idOwner === cxt.user?.id &&
                                            cancelPrivilege)) && (
                                        <Box sx={{ textAlign: 'right' }}>
                                            <SttButton
                                                caption={t(
                                                    'booking.cancelTicket',
                                                )}
                                                onClick={() =>
                                                    cancelTicket(occupant)
                                                }
                                            />
                                        </Box>
                                    )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
